import type { IAcceptChallengeResponse, IGetGameSessionTokenResponse, IGetPrivateRoomIDResponse } from '#api';
import type { IGameData, IIssueChallengeData, IPrivateRoomData } from '#shared/types/games.types';
import type { TBankId } from '#shared/types/user.types';
import { LazyModalFastplayMode, LazyModalGameBuyIn, LazyModalGeneric, LazyModalSignUp } from '#components';
import { EGameCategory } from '#shared/types/games.types';
import { EBankId } from '#shared/types/user.types';
import { stores } from '~/store';

declare global {
  interface Window {
    gameUrlFrame: Window;
  }
}

export const EGameId = {
  MULTIPLINKO: 1,
  ROLL_OR_HOLD: 2,
  ROLLING_21: 5,
  MULTIMINES: 6,
  ESCAPE_THE_CIRCLE: 10,
  MAX_SCARAB_PLINKO: 12,
  MOBILE_WARFARE: 13,
  MAX_PLOSION: 15,
  DUEL_21: 16,
  RUSH_21: 19,
  MAX_SLAM: 20,
  BETBITZ: 21,
  BETBITZ_TURBO: 22,
} as const;

export type TGameId = (typeof EGameId)[keyof typeof EGameId];

interface IGetPrivateRoomData {
  gameId: number;
  bankId: TBankId;
  buyIn: number;
  privateRoomToken: string;
}

interface ConstructGameUrlOptions {
  game: IGameData;
  gameSessionToken: string;
  bankId: number;
  homeUrl?: string;
  buyIn?: number;
  cafParams?: CAFLaunchParams;
}

interface GameParams {
  gameid: number;
  bankId: number;
  token: string;
  homeUrl: string;
  mode: 'real';
  lang: 'en';
  displayName?: string;
  idToken?: string;
  addFriendUrl?: string;
  buyIn?: number;
  createPrivateRoom: boolean;
  privateRoomHostId?: string;
  privateRoomId?: string;
}

export interface GameWindowData {
  url: string;
  gameId: number;
  bankId: TBankId;
}

export interface CAFLaunchParams {
  createPrivateRoom: boolean;
  privateRoomHostId: string;
  privateRoomId?: string;
}

export interface LaunchGameOptions {
  game: IGameData;
  buyIn?: number;
  bankId?: TBankId;
  cafParams?: CAFLaunchParams;
  isDemoPlay?: boolean;
  coin?: string | number;
  useZeroBuyIn?: boolean;
}

export interface PrivateRoomLaunchParams {
  game: IGameData;
  roomId: string;
  bankId: TBankId;
  addFriendUrl?: string;
}

export const DEMO_PLAY_BUY_IN_AMOUNT = 100;

const PLAY_MONEY_ID = 'PM';

export function useGameLauncher() {
  const fastPlayModeLsKey = computed(() => {
    if (!stores.user.isLoggedIn && !stores.user.getUserId()) return undefined;
    return `skip_fp_mode_popup_${stores.user.getUserId()}`;
  });

  function openSignUpModal() {
    stores.modals.open('sign-up', LazyModalSignUp);
  }

  async function openGame(url: string, gameId: number, bankId: TBankId) {
    console.log('[GameLauncher] openGame', { gameId, bankId, url });
    stores.games.setGameData({ url, gameId, bankId });
    await navigateTo('/game');
  }

  async function handleGameClick(game: IGameData) {
    if (!game) return;

    if (!stores.user.isLoggedIn) {
      openSignUpModal();
      return;
    }

    const dollarBalance = stores.user.totalBalance || 0;
    if (dollarBalance < DEMO_PLAY_BUY_IN_AMOUNT) {
      await stores.modals.showInsufficientFundsModal({ game });
      return;
    }

    if (game.category === EGameCategory.SoloPlay) {
      await stores.modals.open('soloplay-mode', LazyModalFastplayMode, { game });
      return;
    }

    if (game.category === EGameCategory.PlayerVsPlayer) {
      if (game.id === EGameId.BETBITZ_TURBO) {
        await stores.modals.open('betbitz-turbo-mode', LazyModalFastplayMode, { game, useZeroBuyIn: true });
        return;
      }
      await stores.modals.open('LazyModalGameBuyIn', LazyModalGameBuyIn, { game });
      return;
    }

    if (game.category === EGameCategory.ChallengeFriends) {
      console.warn('TODO: open CAF game');
    }

    // if (game.category === EGameCategory.SoloPlay) {
    //   return launchGame({ game });
    // }
  }

  function hasEnoughBalance(bankId: TBankId, buyIn = 0) {
    if (bankId === EBankId.PlayMoney) return true;
    if (stores.user.totalBalance === 0) return false;
    return stores.user.totalBalance >= buyIn;
  }

  async function getGameSessionToken(bankId: TBankId): Promise<string | null> {
    const { $authApi } = useTypedNuxtApp();
    const { apiWalletUrl } = useRuntimeConfig().public;

    try {
      if (!stores.user.getUserId()) {
        throw new Error('getGameSessionToken: user is not logged in Firebase');
      }

      const firebaseToken = await stores.user.getIdToken();
      const data = await $authApi.post<IGetGameSessionTokenResponse>(
        '/gentoken',
        { USERID: firebaseToken, bankId },
        { baseURL: apiWalletUrl },
      );
      return data?.EXTSYSTEM?.RESPONSE?.TOKEN;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function getDefaultGameUrl(game: IGameData) {
    const runtimeConfig = useRuntimeConfig();
    const { gamesDefaultDomain, gamesDefaultPath, gamesPvpPath } = runtimeConfig.public;
    const path = game.category === EGameCategory.PlayerVsPlayer ? gamesPvpPath : gamesDefaultPath;
    return `${gamesDefaultDomain}/${path}`;
  }

  async function constructGameUrl(options: ConstructGameUrlOptions) {
    const auth = useTypedNuxtApp().$firebase.auth;
    const user: any = auth?.currentUser;
    if (!user) {
      throw new Error('getGameSessionToken: user is not logged in Firebase');
    }

    if (!stores.user.getUserId()) {
      throw new Error('getGameSessionToken: user is not logged in Firebase');
    }

    const game = options.game;

    const launchUrl = new URL(game.launchUrl || getDefaultGameUrl(game));
    const displayName = user.displayName || '';
    const idToken = (await stores.user.getIdToken()) || '';
    const { createPrivateRoom, privateRoomHostId, privateRoomId } = options.cafParams || {};

    const params: GameParams = {
      gameid: game.id,
      bankId: options.bankId,
      token: options.gameSessionToken,
      homeUrl: options.homeUrl || game.backButtonURL || window.location.href,
      createPrivateRoom: createPrivateRoom || false,
      buyIn: options.buyIn || undefined,
      mode: 'real',
      lang: 'en',
    };

    if (game.paramsType === 'extended') {
      params.displayName = displayName;
      params.idToken = idToken;
      // we actually do not use this param since we open add friends modal by using postMessage type 'openAddFriendsDialog'
      params.addFriendUrl = `${window.location.origin}/addFriends`;
    }

    // if (game.category === EGameCategory.PlayerVsPlayer && options.buyIn) {
    //   params.buyIn = options.buyIn;
    // }

    if (privateRoomHostId) {
      params.privateRoomHostId = privateRoomHostId;
    }

    if (privateRoomId) {
      params.privateRoomId = privateRoomId;
    }

    launchUrl.search = new URLSearchParams(params as unknown as Record<string, string>).toString();
    return launchUrl.toString();
  }

  async function hasResponsibleGamingTimeout() {
    const { timeoutTimestamp, getPlayTimeout } = useResponsibleGaming();

    if (timeoutTimestamp.value === undefined) {
      await getPlayTimeout();
    }

    return timeoutTimestamp.value > Date.now();
  }

  async function launchGame(options: LaunchGameOptions) {
    const userId = stores.user.getUserId();
    if (!userId) return;

    if (await hasResponsibleGamingTimeout()) {
      await stores.modals.open('rg-timeout', LazyModalGeneric, {
        title: 'Game Temporarily Unavailable',
        text: 'Responsible Gaming timeout is active.<br />Please check your account for the end date<br />or contact support.',
      });
      return;
    }

    const game = options.game;
    const cafParams = options.cafParams;
    const isDemoPlay = options.isDemoPlay || false;
    const useZeroBuyIn = options.useZeroBuyIn || false;
    const coin = options.coin;
    let buyIn = options.buyIn || 0;

    let bankId: TBankId;

    if (isDemoPlay) {
      bankId = EBankId.PlayMoney;

      if (game.category === EGameCategory.PlayerVsPlayer && !buyIn && !useZeroBuyIn) {
        buyIn = DEMO_PLAY_BUY_IN_AMOUNT;
      }
    } else if (coin) {
      // old hack for CAF rooms
      bankId = coin === PLAY_MONEY_ID || coin === EBankId.PlayMoney ? EBankId.PlayMoney : EBankId.Dollars;
    } else {
      bankId = options.bankId || stores.user.activeBankId;
    }

    if (!hasEnoughBalance(bankId, buyIn)) {
      await stores.modals.showInsufficientFundsModal({ game });
      return;
    }

    stores.games.setGameIsLaunching(true);
    console.log('[GameLauncher] launchGame:start', {
      gameId: game.id,
      techName: game.techName,
      category: game.category,
      launchUrl: game.launchUrl,
      paramsType: game.paramsType,
      unityGame: game.unityGame,
      buyIn,
      bankId,
    });

    const gameSessionToken = await getGameSessionToken(bankId);
    if (!gameSessionToken) {
      stores.games.setGameIsLaunching(false);
      console.error('TODO: failed to get game session token');
      return;
    }

    const gameUrl = await constructGameUrl({ game, gameSessionToken, bankId, buyIn, cafParams });
    console.log('[GameLauncher] launchGame:constructedUrl', {
      gameId: game.id,
      url: gameUrl,
    });

    if (game.unityGame && cafParams) {
      await stores.games.setLoadingClient(userId, game.id);
    }

    await openGame(gameUrl, game.id, bankId);

    stores.games.setGameIsLaunching(false);
  }

  async function enterPrivateRoom(params: PrivateRoomLaunchParams) {
    const { game, roomId, bankId, addFriendUrl } = params;

    if (!hasEnoughBalance(bankId)) {
      await stores.modals.showInsufficientFundsModal();
      return;
    }

    stores.games.setPrivateRoomId(roomId);

    const gameSessionToken = await getGameSessionToken(bankId);
    if (!gameSessionToken) {
      console.error('Failed to get game session token');
      await stores.modals.open('get-game-session-error', LazyModalGeneric, {
        title: 'Failed to get game session token',
        text: 'Please try again later',
      });
      return;
    }

    const legacyOrigin = new URL(game.launchUrl).origin;
    const cafLaunchPath = useRuntimeConfig().public.legacyGamesCAFPath as string;
    const gameURL = new URL(cafLaunchPath, legacyOrigin);

    const searchParams = new URLSearchParams({
      homeUrl: window.location.href,
      mmcToken: gameSessionToken,
      mqcToken: gameSessionToken,
      privateRoomId: roomId,
      addFriendUrl: addFriendUrl || `${window.location.origin}/addFriends`,
      lang: 'en',
    });
    gameURL.search = searchParams.toString();

    await openGame(gameURL.toString(), game.id, bankId);
  }

  function disableFastplayModeModal() {
    if (!fastPlayModeLsKey.value) return;
    localStorage.setItem(fastPlayModeLsKey.value, '1');
  }

  async function getPrivateRoomID(gameData: IGetPrivateRoomData) {
    const { $authApi } = useTypedNuxtApp();
    const hostId = stores.user.getUserId();

    let privateRoomId = '';
    let privateRoomData: IPrivateRoomData;
    let message = '';

    const sessionKey = await stores.user.getIdToken();

    const postData = { ...gameData, sessionKey, hostId };
    const { privateRoomToken, gameId, bankId, buyIn } = postData;

    try {
      const response = await $authApi.post<IGetPrivateRoomIDResponse>('/getPrivateRoomID', {
        privateRoomToken,
        gameId,
        bankId,
        buyIn,
        hostId,
        sessionKey,
      });

      privateRoomId = response.privateRoomId || '';
      privateRoomData = response.privateRoomData;
    } catch (error: any) {
      message = error.response?.data?.message || 'getPrivateRoomID failed';
      console.error('error creating private room id', error);
    }

    return {
      message,
      privateRoomId,
      privateRoomData,
    };
  }

  async function issueChallenge(privateRoomData: IIssueChallengeData) {
    const { $authApi, $config } = useTypedNuxtApp();
    const { token, privateRoomDbData, addFriendUrl, selectedFriends, gameOrigin } = privateRoomData;

    const privateRoomId = privateRoomDbData.privateRoomId;
    const homeUrl = `${window.location.origin}/challenge-friends`;
    const searchParams = new URLSearchParams({
      mmcToken: token,
      mqcToken: token,
      lang: 'en',
      privateRoomId,
      homeUrl,
      addFriendUrl,
    });

    const privateRoomURL = `${gameOrigin}/${$config.public.legacyGamesCAFPath}?${searchParams.toString()}`;

    try {
      const sessionKey = await stores.user.getIdToken();
      const data = await $authApi.post<IAcceptChallengeResponse>('/inviteFriendsPrivateRoom', {
        selectedFriends,
        privateRoomDbData,
        sessionKey,
      });

      return { ...data, privateRoomURL };
    } catch (error: any) {
      console.error(error);

      return {
        status: 'ERROR',
        message: error.message || 'Failed inviting friends to private room',
      };
    }
  }

  return {
    handleGameClick,
    launchGame,
    enterPrivateRoom,
    disableFastplayModeModal,
    getGameSessionToken,
    getPrivateRoomID,
    issueChallenge,
    openGame,
  };
}
